import toast from "react-hot-toast";
import { Warning } from "@phosphor-icons/react";
import { CheckCircle, X } from "@phosphor-icons/react";
import { isMobile } from "./helpers";

const isCompactViewport = () => isMobile() || (typeof window !== "undefined" && window.innerWidth <= 768);

const getV2ContainerClass = (visible: boolean, compact: boolean, isError: boolean) => {
  const shadowClass = isError ? "shadow-toast-error" : "shadow-toast-success";
  const baseClasses = `pointer-events-auto relative flex items-center bg-white transition-all ${visible ? "animate-enter" : "animate-leave"} ${shadowClass}`;

  if (compact) {
    return `${baseClasses} w-full max-w-full rounded-none px-0 py-0`;
  }
  return `${baseClasses} w-[calc(100vw-24px)] max-w-sm rounded-2xl px-3 py-3`;
};

const getV2CustomStyle = (compact: boolean) =>
  compact
    ? {
        width: "100vw",
        maxWidth: "100vw",
        margin: `0px calc(50% - 50vw)`,
        padding: "0px",
      }
    : undefined;

const renderBannerToastV2 = (msg: string, toastId: string, visible: boolean, isError: boolean) => {
  const compact = isCompactViewport();
  const Icon = isError ? Warning : CheckCircle;
  const iconColorClass = isError ? "text-red-600" : "text-green-600";
  const textColorClass = isError ? "text-red-700" : "text-green-700";

  return (
    <div className={getV2ContainerClass(visible, compact, isError)}>
      <div className={compact ? "flex h-full w-12 items-center justify-center py-3.5" : "mr-4 flex items-center justify-center"}>
        <Icon size={compact ? 24 : 30} className={iconColorClass} weight={compact ? "regular" : "duotone"} />
      </div>
      <div className={compact ? "min-w-0 flex-1 py-3 pr-3" : "min-w-0 flex-1"}>
        <p className={`text-[14px] leading-[22px] tracking-[-0.01em] font-semibold ${textColorClass}`}>
          {msg}
        </p>
      </div>
      <div className={compact ? "flex h-full items-center pr-2" : "ml-4 flex items-center"}>
        <button
          type="button"
          className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
          onClick={() => toast.dismiss(toastId)}
          aria-label="Dismiss notification">
          <X size={compact ? 18 : 24} />
        </button>
      </div>
    </div>
  );
};

export const successToast = (msg: string, duration: number = 3000) => {
  toast.dismiss();
  toast.success(msg, {
    duration: duration,
    position: "bottom-center",
    style: {
      borderRadius: "10px",
      background: "#333",
      color: "#fff",
    },
  });
};

/** V2 banner-style success toast (desktop + mobile variants). */
export const bannerSuccessToastV2 = (msg: string, duration: number = 3000) => {
  toast.dismiss();
  const compact = isCompactViewport();
  toast.custom(
    (t) => renderBannerToastV2(msg, t.id, t.visible, false),
    {
      duration,
      position: "bottom-center",
      ...(compact ? { style: getV2CustomStyle(compact) } : {}),
    },
  );
};

/** V2 banner-style error toast (desktop + mobile variants). */
export const bannerErrorToastV2 = (msg: string = "Error. Please try later.", duration: number = 3000) => {
  toast.dismiss();
  const compact = isCompactViewport();
  toast.custom(
    (t) => renderBannerToastV2(msg, t.id, t.visible, true),
    {
      duration,
      position: "bottom-center",
      ...(compact ? { style: getV2CustomStyle(compact) } : {}),
    },
  );
};

export const errorToast = (msg: string = "Error. Please try later.", duration: number = 3000) => {
  toast.dismiss();
  
  toast.error(msg, {
    duration: duration,
    position: "bottom-center",
    style: {
      borderRadius: "10px",
      background: "#333",
      color: "#fff",
    },
  });
};

export const infoToast = (msg: string, duration: number = 3000) => {
  toast.dismiss();
  toast(msg, {
    icon: "ⓘ",
    duration: duration,
    position: "bottom-center",
    style: {
      borderRadius: "10px",
      background: "#333",
      color: "#fff",
      fontSize: "12px",
    },
  });
};

export const checkoutInfoToast = (msg: string, duration: number = 3000) => {
  toast.dismiss();
  toast(msg, {
    icon: <Warning size={16} className="text-white" />,
    duration: duration,
    position: "bottom-center",
    style: {
      borderRadius: "10px",
      background: "#444",
      color: "#fff",
      fontSize: "12px",
    },
  });
};
